import { el, setStyle, setClass, damp } from './util.js';

/**
 * Touch controls for coarse-pointer devices (instantiated by UiSystem only
 * when `isTouchDevice()` — desktop never sees this overlay).
 *
 *   left zone    dynamic virtual joystick: the base appears where the thumb
 *                lands, deflection feeds `input.touch` (gamepad convention,
 *                so full-forward auto-sprints through the existing stick path)
 *   right zone   drag to look; deltas join the mouse path via `input.addLook`
 *   buttons      synthesize the same codes the keyboard/mouse bind, so every
 *                downstream consumer (weapons, movement, menu) works unchanged.
 *                FIRE is hold-to-fire, ADS is a toggle, the rest are momentary.
 *
 * All listeners preventDefault so no compatibility mouse events reach the
 * canvas (which would request pointer lock and double-fire Mouse0).
 */

const STICK_RADIUS = 56; // px @1080p, scaled by --k
const LOOK_SCALE = 2.1; // touch px feel slower than mouse counts; compensate

export class TouchControls {
  constructor(parent, ctx) {
    this.ctx = ctx;
    this.root = el('div', 'ow-touch', parent);
    this.shown = 1;

    // ---- zones -----------------------------------------------------------
    this.moveZone = el('div', 'ow-touch-zone move', this.root);
    this.lookZone = el('div', 'ow-touch-zone look', this.root);
    this.stickEl = el('div', 'ow-stick', this.moveZone);
    this.nubEl = el('div', 'ow-stick-nub', this.stickEl);
    setStyle(this.stickEl, 'opacity', '0');

    this._movePid = null;
    this._moveOrigin = { x: 0, y: 0 };
    this._lookPid = null;
    this._lookLast = { x: 0, y: 0 };

    this._on(this.moveZone, 'pointerdown', (e) => this._moveStart(e));
    this._on(this.moveZone, 'pointermove', (e) => this._moveDrag(e));
    this._on(this.moveZone, 'pointerup', (e) => this._moveEnd(e));
    this._on(this.moveZone, 'pointercancel', (e) => this._moveEnd(e));

    this._on(this.lookZone, 'pointerdown', (e) => {
      if (this._lookPid !== null) return;
      this._lookPid = e.pointerId;
      this._capture(this.lookZone, e);
      this._lookLast.x = e.clientX;
      this._lookLast.y = e.clientY;
    });
    this._on(this.lookZone, 'pointermove', (e) => {
      if (e.pointerId !== this._lookPid) return;
      this.ctx.input.addLook?.(
        (e.clientX - this._lookLast.x) * LOOK_SCALE,
        (e.clientY - this._lookLast.y) * LOOK_SCALE
      );
      this._lookLast.x = e.clientX;
      this._lookLast.y = e.clientY;
    });
    const lookEnd = (e) => {
      if (e.pointerId === this._lookPid) this._lookPid = null;
    };
    this._on(this.lookZone, 'pointerup', lookEnd);
    this._on(this.lookZone, 'pointercancel', lookEnd);

    // ---- buttons ---------------------------------------------------------
    this._buttons = [];
    this._hold('FIRE', 'fire', 'Mouse0');
    this.adsBtn = this._toggle('ADS', 'ads', 'Mouse2');
    this._hold('JUMP', 'jump', 'Space');
    this._hold('RLD', 'rld', 'KeyR');
    this._hold('CRCH', 'crch', 'KeyC');
    this._hold('SWAP', 'swap', 'Tab');
    this._tap('| |', 'pause', 'Escape');

    this._adsOn = false;
    this._adsFrames = 0;

    // Kill any residual browser gesture (double-tap zoom, synthetic clicks).
    this._touchBlock = (e) => e.preventDefault();
    this.root.addEventListener('touchstart', this._touchBlock, { passive: false });
  }

  _on(node, type, fn) {
    node.addEventListener(type, (e) => {
      e.preventDefault();
      fn(e);
    });
  }

  _capture(node, e) {
    try {
      node.setPointerCapture(e.pointerId);
    } catch {
      /* pointer already lifted — nothing to capture */
    }
  }

  _hold(label, cls, code) {
    const b = el('div', 'ow-tbtn ' + cls, this.root, label);
    this._on(b, 'pointerdown', (e) => {
      this._capture(b, e);
      this.ctx.input.simulateDown?.(code);
      setClass(b, 'held', true);
    });
    const up = () => {
      this.ctx.input.simulateUp?.(code);
      setClass(b, 'held', false);
    };
    this._on(b, 'pointerup', up);
    this._on(b, 'pointercancel', up);
    this._buttons.push(b);
    return b;
  }

  /** Down+up queued together: one clean press edge even though opening the
   *  menu hides this overlay before the finger lifts. */
  _tap(label, cls, code) {
    const b = el('div', 'ow-tbtn ' + cls, this.root, label);
    this._on(b, 'pointerdown', () => {
      this.ctx.input.simulateDown?.(code);
      this.ctx.input.simulateUp?.(code);
    });
    this._buttons.push(b);
    return b;
  }

  _toggle(label, cls, code) {
    const b = el('div', 'ow-tbtn ' + cls, this.root, label);
    this._on(b, 'pointerdown', (e) => {
      this._capture(b, e);
      this._adsOn = !this._adsOn;
      this._adsFrames = 0;
      const input = this.ctx.input;
      this._adsOn ? input.simulateDown?.(code) : input.simulateUp?.(code);
      setClass(b, 'on', this._adsOn);
    });
    this._buttons.push(b);
    return b;
  }

  /* ------------------------------------------------------------ joystick -- */

  _moveStart(e) {
    if (this._movePid !== null) return;
    this._movePid = e.pointerId;
    this._capture(this.moveZone, e);
    // Nub travel = base circle radius minus a rim; measured so it always
    // matches whatever --tk resolved to in CSS.
    this._r = this.stickEl.getBoundingClientRect().width * 0.45 || STICK_RADIUS;
    this._moveOrigin.x = e.clientX;
    this._moveOrigin.y = e.clientY;
    const zone = this.moveZone.getBoundingClientRect();
    setStyle(
      this.stickEl,
      'transform',
      `translate(${(e.clientX - zone.left).toFixed(1)}px, ${(e.clientY - zone.top).toFixed(1)}px)`
    );
    setStyle(this.stickEl, 'opacity', '1');
    setStyle(this.nubEl, 'transform', 'translate(0px, 0px)');
  }

  _moveDrag(e) {
    if (e.pointerId !== this._movePid) return;
    let dx = e.clientX - this._moveOrigin.x;
    let dy = e.clientY - this._moveOrigin.y;
    const r = this._r || STICK_RADIUS;
    const len = Math.hypot(dx, dy);
    if (len > r) {
      dx *= r / len;
      dy *= r / len;
    }
    setStyle(this.nubEl, 'transform', `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`);
    this.ctx.input.touch.moveX = dx / r;
    this.ctx.input.touch.moveY = dy / r;
  }

  _moveEnd(e) {
    if (e.pointerId !== this._movePid) return;
    this._movePid = null;
    this.ctx.input.touch.moveX = 0;
    this.ctx.input.touch.moveY = 0;
    setStyle(this.stickEl, 'opacity', '0');
  }

  /* --------------------------------------------------------------- frame -- */

  /** Unscaled dt so the fade runs while paused, like the menu. */
  update(rawDt, menuOpen) {
    this.shown = damp(this.shown, menuOpen ? 0 : 1, 14, rawDt);
    const hidden = this.shown < 0.02;
    setStyle(this.root, 'opacity', hidden ? '0' : this.shown.toFixed(3));
    setStyle(this.root, 'visibility', hidden ? 'hidden' : 'visible');

    // A window blur force-releases every held code (see Input._onBlur); the
    // ADS toggle must follow or its visual state lies.
    if (this._adsOn && ++this._adsFrames > 2 && !this.ctx.input.held('Mouse2')) {
      this._adsOn = false;
      setClass(this.adsBtn, 'on', false);
    }
  }

  dispose() {
    const t = this.ctx.input?.touch;
    if (t) t.moveX = t.moveY = 0;
    this.root.removeEventListener('touchstart', this._touchBlock);
    this.root.remove();
  }
}
